<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, useHttpClient, useHttpGet } from "@/vf"
import { computed, ref } from "vue"

type RefundBoxData = {
    canRefund: boolean
    molliePaidTotal: number
    mollieRefundedTotal: number
    mollieRefundableBalance: number
}

type RefundResponse = {
    requestedAmount: number
    refundedAmount: number
    remainingAmount: number
}

const props = defineProps<{
    dataEndpoint: string
    refundEndpoint: string
}>()

const emit = defineEmits<{
    (e: "refunded"): void
}>()

const trans = defineTranslations({
    "title": {
        de: "Rückerstattung",
        en: "Refund",
    },
    "paid_total": {
        de: "Via Mollie bezahlt",
        en: "Paid via Mollie",
    },
    "refunded_total": {
        de: "Bereits rückerstattet",
        en: "Already refunded",
    },
    "refundable_balance": {
        de: "Verfügbar zur Rückerstattung",
        en: "Available to refund",
    },
    "amount": {
        de: "Rückerstattungsbetrag",
        en: "Refund amount",
    },
    "action.refund": {
        de: "Rückerstatten",
        en: "Refund",
    },
    "confirm": {
        de: "Sollen {amount} wirklich rückerstattet werden?",
        en: "Do you really want to refund {amount}?",
    },
    "success": {
        de: "Rückerstattung erfolgreich erstellt.",
        en: "Refund created successfully.",
    },
    "result.full": {
        de: "{refunded} wurden vollständig rückerstattet.",
        en: "{refunded} was refunded successfully.",
    },
    "result.partial": {
        de: "{refunded} wurden rückerstattet. {remaining} konnten aktuell nicht rückerstattet werden. Bitte versuche es in ein paar Minuten erneut.",
        en: "{refunded} was refunded. {remaining} could not be refunded right now. Please retry in a few minutes.",
    },
})

const http = useHttpClient()
const { data, loading, refresh, statusCode } = useHttpGet<RefundBoxData>(
    computed(() => props.dataEndpoint),
    false,
)
const refundAmount = ref<number>()
const lastRefundResult = ref<RefundResponse>()

const refundAmountCents = computed(() => {
    if (!refundAmount.value || refundAmount.value <= 0) {
        return 0
    }

    return Math.round(refundAmount.value * 100)
})

const canSubmit = computed(() => {
    const available = data.value?.mollieRefundableBalance ?? 0
    return (data.value?.canRefund ?? false) && refundAmountCents.value > 0 && refundAmountCents.value <= available
})

const refundConfirm = createInlineConfirm({
    async action() {
        if (!canSubmit.value) {
            return false
        }

        const { data } = await http.post<RefundResponse>(props.refundEndpoint, {
            amount: refundAmountCents.value,
        })

        lastRefundResult.value = data

        return true
    },
    async afterAction() {
        refundAmount.value = undefined
        await refresh()
        emit("refunded")
    },
})

const refundResultMessage = computed(() => {
    if (!lastRefundResult.value) {
        return ""
    }

    if (lastRefundResult.value.remainingAmount > 0) {
        return trans("result.partial", {
            refunded: $currency(lastRefundResult.value.refundedAmount),
            remaining: $currency(lastRefundResult.value.remainingAmount),
        })
    }

    return trans("result.full", {
        refunded: $currency(lastRefundResult.value.refundedAmount),
    })
})

const refundResultAlertClass = computed(() => {
    if (!lastRefundResult.value) {
        return "alert-success"
    }

    return lastRefundResult.value.remainingAmount > 0 ? "alert-danger" : "alert-success"
})
</script>

<template>
    <div v-if="!loading && statusCode !== 403 && data && data.molliePaidTotal > 0">
        <div class="bg-primary-dark text-white px-4 py-2 font-weight-bold">
            {{ trans("title") }}
        </div>

        <div class="p-4 border border-gray border-top-0">
            <table class="table table-borderless mb-0">
                <tbody>
                    <tr>
                        <th>{{ trans("paid_total") }}</th>
                        <td class="text-right">
                            {{ $currency(data.molliePaidTotal, 100, { currencyDisplay: "name" }) }}
                        </td>
                    </tr>
                    <tr>
                        <th>{{ trans("refunded_total") }}</th>
                        <td class="text-right">
                            {{ $currency(data.mollieRefundedTotal, 100, { currencyDisplay: "name" }) }}
                        </td>
                    </tr>
                    <tr>
                        <td colspan="2" class="text-center py-0"><hr class="my-1" /></td>
                    </tr>
                    <tr>
                        <th>{{ trans("refundable_balance") }}</th>
                        <td class="text-right">
                            <a href="#" @click.prevent="refundAmount = data.mollieRefundableBalance / 100">
                                {{ $currency(data.mollieRefundableBalance, 100, { currencyDisplay: "name" }) }}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="data.canRefund && data.mollieRefundableBalance > 0" class="px-2">
                <b class="mt-4 d-block">{{ trans("amount") }}</b>
                <div class="input-group">
                    <input
                        v-model.number="refundAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        class="form-control d-inline-block text-right"
                        placeholder="0,00"
                    />
                    <div class="input-group-append">
                        <div class="input-group-text border-0">Euro</div>
                    </div>
                </div>

                <div v-if="refundResultMessage" class="alert mt-3 mb-0" :class="refundResultAlertClass">
                    {{ refundResultMessage }}
                </div>

                <div class="mt-4 text-right">
                    <VarsityInlineConfirmButton
                        :controller="refundConfirm"
                        :disabled="!canSubmit"
                        class="btn btn-shape-skewed btn-danger"
                    >
                        {{ trans("action.refund") }}
                    </VarsityInlineConfirmButton>
                </div>

                <VarsityInlineConfirm :controller="refundConfirm" danger>
                    <template #confirmation>
                        {{ trans("confirm", { amount: $currency(refundAmountCents) }) }}
                    </template>
                </VarsityInlineConfirm>
            </div>
        </div>
    </div>
</template>
